import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  TemplateCardComponent,
  TemplateData,
} from '../../../shared/components/template-card/template-card.component';

/**
 * A reusable panel component that displays a list of templates, handles user interactions for selecting or previewing a template, and notifies the parent when the panel should be closed.
 * @component
 * @selector app-templates-panel
 * @example
 *   <app-templates-panel
 *     [templates]="templates"
 *     [loading]="isLoading"
 *     [error]="errorMessage"
 *     (templateSelect)="handleSelect($event)"
 *     (templatePreview)="handlePreview($event)"
 *     (close)="handleClose()"
 *   ></app-templates-panel>
 * @input TemplateData[] templates - Array of template objects to render as cards in the panel.
 * @input boolean loading - Controls whether the panel shows a loading state instead of the template list.
 * @input string|null error - Optional error message to display when template retrieval or rendering fails.
 * @output EventEmitter<TemplateData> templateSelect - Emitted when a template is selected. The payload is the selected TemplateData. The parent component is expected to manage selection state and any confirmation flow.
 * @output EventEmitter<TemplateData> templatePreview - Emitted when the user requests a preview of a template. The payload is the TemplateData to preview.
 * @output EventEmitter<void> close - Emitted to request closing the templates panel.
 *
 * Key methods:
 * - onTemplateSelect(template: TemplateData): void — Emits templateSelect with the chosen template. Intentionally does not mutate local selection state so the parent can handle confirmation and state updates.
 * - onTemplatePreview(template: TemplateData): void — Emits templatePreview to trigger a preview workflow in the parent.
 * - onClose(): void — Emits close to signal that the panel should be closed.
 * - trackByTemplateId(index: number, template: TemplateData): string — Returns template.id to optimize ngFor rendering and minimize DOM churn.
 *
 * Lifecycle:
 * - This standalone component does not implement Angular lifecycle hooks itself; it relies on input bindings and parent-driven state management for initialization and updates.
 */
@Component({
  selector: 'app-templates-panel',
  standalone: true,
  imports: [CommonModule, TemplateCardComponent],
  templateUrl: './templates-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./templates-panel.component.scss'],
})
export class TemplatesPanelComponent {
  @Input() templates: TemplateData[] = [];
  @Input() loading = false;
  @Input() error: string | null = null;
  @Output() templateSelect = new EventEmitter<TemplateData>();
  @Output() templatePreview = new EventEmitter<TemplateData>();
  @Output() close = new EventEmitter<void>();

  onTemplateSelect(template: TemplateData): void {
    // Don't update selection state here - let the parent component handle it
    // This allows the parent to show a confirmation modal before changing selection
    this.templateSelect.emit(template);
  }

  onTemplatePreview(template: TemplateData): void {
    this.templatePreview.emit(template);
  }

  onClose(): void {
    this.close.emit();
  }

  trackByTemplateId(index: number, template: TemplateData): string {
    return template.id;
  }
}
